import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { queryClient } from "@/lib/queryClient";
import { invalidateAcornRulesCache } from "@/lib/acornSubscribers";
import type { MoriEventName } from "@/lib/events";

export interface AcornRule {
  id: string;
  event_name: string;
  condition_key: string | null;
  condition_value: string | null;
  acorn_amount: number;
  label: string;
  active: boolean;
  sort_order: number;
}

export interface NewAcornRule {
  event_name: MoriEventName;
  condition_key: string;
  condition_value: string;
  acorn_amount: number;
  label: string;
}

export function invalidateAcornRules() {
  return queryClient.invalidateQueries({ queryKey: ["acorn-rules"] });
}

export function useAcornRules() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["acorn-rules"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("acorn_rules")
        .select("*")
        .order("sort_order");
      if (error) throw error;
      return (data as AcornRule[]) ?? [];
    },
  });

  const saveRuleMutation = useMutation({
    mutationFn: async (rule: AcornRule) => {
      const { error } = await supabase
        .from("acorn_rules")
        .update({
          acorn_amount: rule.acorn_amount,
          label: rule.label,
          active: rule.active,
          updated_at: new Date().toISOString(),
        })
        .eq("id", rule.id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAcornRulesCache();
      void qc.invalidateQueries({ queryKey: ["acorn-rules"] });
      toast.success(
        "Rule saved. Changes take effect within 5 minutes for all users.",
      );
    },
    onError: (error) => {
      toast.error("Save failed: " + (error as Error).message);
    },
  });

  const addRuleMutation = useMutation({
    mutationFn: async (newRule: NewAcornRule) => {
      const sort_order = (query.data?.length ?? 0) + 1;
      const { error } = await supabase.from("acorn_rules").insert({
        event_name: newRule.event_name,
        condition_key: newRule.condition_key.trim() || null,
        condition_value: newRule.condition_value.trim() || null,
        acorn_amount: newRule.acorn_amount,
        label: newRule.label.trim(),
        active: true,
        sort_order,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAcornRulesCache();
      void qc.invalidateQueries({ queryKey: ["acorn-rules"] });
      toast.success("Rule added.");
    },
    onError: (error) => {
      toast.error("Failed to add rule: " + (error as Error).message);
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("acorn_rules")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateAcornRulesCache();
      void qc.invalidateQueries({ queryKey: ["acorn-rules"] });
      toast.success("Rule deleted.");
    },
    onError: (error) => {
      toast.error("Failed to delete rule: " + (error as Error).message);
    },
  });

  return {
    data: query.data,
    rules: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
    saveRule: (rule: AcornRule) => saveRuleMutation.mutateAsync(rule),
    addRule: (newRule: NewAcornRule) => addRuleMutation.mutateAsync(newRule),
    deleteRule: (id: string) => deleteRuleMutation.mutateAsync(id),
  };
}
